import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataFormComponent } from './data-form.component';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { ObjectTypeComponent } from './types/object.type';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ArrayTypeComponent } from './types/array.type';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MapLocationComponent } from './map-location/map-location.component';
import { MatInputModule } from '@angular/material/input';
import { MatMomentDateModule, MomentDateModule } from '@angular/material-moment-adapter';
import { ColorPickerCustomComponent } from './types/color-picker.type';
import { FieldRichTextType } from './types/rich-text.type';
import moment from 'moment';
import { SelectWithSearchComponent } from 'app/shared/components/data-form/types/select-with-search/select-with-search.component';
import { FormlyCurrencyTypeComponent } from 'app/shared/components/data-form/types/currency.type';
import { FormlyDateRangeComponent } from 'app/shared/components/data-form/types/date-range/date-range.component';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AutoCompleteTypeComponent } from 'app/shared/autocomplete-type/autocomplete-type.component';

export const minlengthValidationMessages = (err, field): string => `Should have atleast ${field.templateOptions.minLength} characters`;

export const parseDate = (value: any): any => moment(value).format('YYYY-MM-DD');

export const fieldMatchValidator = (control: AbstractControl): any => {
    const { password, passwordConfirm } = control?.value || '';
    // avoid displaying the message error when values are empty
    if (!passwordConfirm || !password) {
        return null;
    }
    if (passwordConfirm === password) {
        return null;
    }
    return { fieldMatch: { message: 'Password Not Matching' } };
};

@NgModule({
    exports: [
        DataFormComponent,
        MapLocationComponent,
        FieldRichTextType
    ],
    imports: [
        CommonModule,
        FormsModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatCardModule,
        MatTooltipModule,
        FormlyModule,
        MomentDateModule,
        MatMomentDateModule,
        FormlyModule.forChild({
            validators: [
                { name: 'fieldMatch', validation: fieldMatchValidator },
                {
                    name: 'max4Decimals',
                    validation: control =>
                        !control.value || /^\d+(\.\d{1,4})?$/.test(control.value),
                },
            ],
            types: [
                {
                    name: 'quill',
                    component: FieldRichTextType,
                    wrappers: ['form-field'],
                },
                {
                    name: 'string', extends: 'input', defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'input', defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'textarea', defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'select', defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'number',
                    extends: 'input',
                    defaultOptions: {
                        templateOptions: {
                            type: 'number',
                            appearance: 'outline',
                        },
                    },
                },
                {
                    name: 'integer',
                    extends: 'input',
                    defaultOptions: {
                        templateOptions: {
                            type: 'number',
                            step: 1,
                            appearance: 'outline',
                        },
                    },
                },
                {
                    name: 'select-with-search',
                    component: SelectWithSearchComponent,
                    defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'date-range',
                    component: FormlyDateRangeComponent,
                    defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'customDatePicker', extends: 'datePicker', defaultOptions: {
                        parsers: [parseDate], templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'boolean',
                    extends: 'checkbox',
                    defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                {
                    name: 'checkbox',
                    defaultOptions: {
                        templateOptions: {
                            appearance: 'outline',
                        }
                    }
                },
                { name: 'currency', component: FormlyCurrencyTypeComponent, wrappers: [] },
                { name: 'enum', extends: 'select' },
                { name: 'object', component: ObjectTypeComponent },
                { name: 'array', component: ArrayTypeComponent },
                { name: 'mapLocation', component: MapLocationComponent },
                { name: 'colorPicker', component: ColorPickerCustomComponent },
                { name: 'autocompleteType', component: AutoCompleteTypeComponent }
                // {
                //     name: 'phone',
                //     component: PhoneMaskTypeComponent,
                //     wrappers: ['form-field'],
                //     defaultOptions: {
                //         templateOptions: {
                //             type: 'phone',
                //         },
                //     },
                // },
                // {
                //     name: 'datepicker',
                //     component: DatepickerTypeComponent,
                //     defaultOptions: { templateOptions: { datepickerOptions: {} } }
                // },
            ],
            validationMessages: [
                { name: 'minlength', message: minlengthValidationMessages },
                {
                    name: 'max4Decimals',
                    message: 'Maximum 4 decimal places allowed',
                },
            ],
        }),
        FormlyMaterialModule
        // , AgmCoreModule.forRoot({
        //     apiKey: environment.mapKey,
        //     libraries: ['places']
        // })
        ,
        NgxMatSelectSearchModule,
        MatDatepickerModule,
        DataFormComponent,
        ObjectTypeComponent,
        ArrayTypeComponent,
        MapLocationComponent,
        ColorPickerCustomComponent,
        FieldRichTextType,
        SelectWithSearchComponent,
        FormlyDateRangeComponent,
        FormlyCurrencyTypeComponent,
        AutoCompleteTypeComponent
    ]
})
export class DataFormModule {
}
